'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { getRoleRedirect } from '@/contexts/AuthContext';

export default function LoginClient() {
  const router = useRouter();
  const { signIn, profile, loading, session } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [profileTimeout, setProfileTimeout] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (!loading && session && profile) {
      router.replace(getRoleRedirect(profile.role));
    }
  }, [loading, session, profile, router]);

  // If session exists but profile never loads after 6s, show error
  useEffect(() => {
    if (!loading && session && !profile) {
      const timer = setTimeout(() => {
        setProfileTimeout(true);
      }, 6000);
      return () => clearTimeout(timer);
    }
    setProfileTimeout(false);
  }, [loading, session, profile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await signIn(email.trim(), password);
      // Profile will be loaded by AuthContext — redirect handled by useEffect above
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur de connexion';
      if (message.includes('Invalid login credentials')) {
        setError('E-mail ou mot de passe incorrect.');
      } else {
        setError(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--background)' }}>
        <div className="w-8 h-8 border-2 border-[var(--accent)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-12"
      style={{ background: 'var(--background)' }}
    >
      {/* Card */}
      <div
        className="w-full max-w-md rounded-2xl shadow-xl p-8 sm:p-10"
        style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
      >
        {/* Logos */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <Image
            src="/assets/images/Africa_Global_Logistics_logo-1781204269560.png"
            alt="Africa Global Logistics"
            width={80}
            height={40}
            className="object-contain"
          />
          <div className="w-px h-8" style={{ background: 'var(--border)' }} />
          <Image
            src="/assets/images/focus_tl_transparent-1781204226032.png"
            alt="FOCUS TL"
            width={80}
            height={40}
            className="object-contain"
          />
        </div>

        <h1
          className="text-2xl font-bold text-center mb-1"
          style={{ color: 'var(--foreground)' }}
        >
          Connexion
        </h1>
        <p className="text-sm text-center mb-8" style={{ color: 'var(--muted-foreground)' }}>
          Accédez à votre espace GROW FOCUS
        </p>

        {/* Error */}
        {(error || profileTimeout) && (
          <div
            className="mb-5 px-4 py-3 rounded-lg text-sm"
            style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}
          >
            {profileTimeout
              ? 'Connexion réussie mais profil introuvable. Vérifiez que votre compte existe dans la base de données.'
              : error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Email */}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium mb-1.5"
              style={{ color: 'var(--foreground)' }}
            >
              Adresse e-mail
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vous@exemple.com"
              className="w-full px-4 py-2.5 rounded-lg text-sm outline-none transition-all"
              style={{
                background: 'var(--background)',
                border: '1px solid var(--border)',
                color: 'var(--foreground)',
              }}
            />
          </div>

          {/* Password */}
          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium mb-1.5"
              style={{ color: 'var(--foreground)' }}
            >
              Mot de passe
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-4 py-2.5 pr-11 rounded-lg text-sm outline-none transition-all"
                style={{
                  background: 'var(--background)',
                  border: '1px solid var(--border)',
                  color: 'var(--foreground)',
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-sm"
                style={{ color: 'var(--muted-foreground)' }}
                aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-2.5 rounded-lg text-sm font-semibold transition-opacity disabled:opacity-60"
            style={{ background: 'var(--accent)', color: '#fff' }}
          >
            {isSubmitting ? 'Connexion en cours…' : 'Se connecter'}
          </button>
        </form>

        {/* Divider */}
        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>ou</span>
          <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
        </div>

        {/* Apprenant access code link */}
        <Link
          href="/login/code-acces"
          className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg text-sm font-medium transition-colors"
          style={{
            border: '1px solid var(--border)',
            color: 'var(--foreground)',
            background: 'transparent',
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
          </svg>
          Connexion avec un code d'accès (Apprenants)
        </Link>
      </div>

      {/* Footer */}
      <p className="mt-6 text-xs text-center" style={{ color: 'var(--muted-foreground)' }}>
        GROW Pilotage FOCUS — Formation Managériale Certifiante
      </p>
    </div>
  );
}
